import { useEffect, useState } from "react"

export type TranslationStatus =
	| "QUEUED"
	| "TRANSLATING"
	| "COMPLETED"
	| "FAILED"
	| null

export interface TranslationProgress {
	translationStatus?: TranslationStatus
	updatedAt?: number | null
}

// A dropped background job leaves the row QUEUED forever, which disables the
// retry button and locks the editor, so an untouched row counts as not running.
export const TRANSLATION_STALE_AFTER_MS = 10 * 60 * 1000

function isPending(status: TranslationStatus | undefined): boolean {
	return status === "QUEUED" || status === "TRANSLATING"
}

export function isTranslationStalled(
	{ translationStatus, updatedAt }: TranslationProgress,
	now: number = Date.now(),
): boolean {
	if (!isPending(translationStatus)) return false
	if (typeof updatedAt !== "number") return false
	return now - updatedAt >= TRANSLATION_STALE_AFTER_MS
}

export function isTranslationRunning(
	progress: TranslationProgress,
	now: number = Date.now(),
): boolean {
	if (!isPending(progress.translationStatus)) return false
	return !isTranslationStalled(progress, now)
}

// Re-checks on a timer so a job that dies while the page is open releases the
// editor without a reload.
export function useIsTranslationRunning(
	progress: TranslationProgress,
): boolean {
	const [now, setNow] = useState(() => Date.now())
	const { updatedAt } = progress
	const isRunning = isTranslationRunning(progress, now)

	useEffect(() => {
		if (!isRunning || typeof updatedAt !== "number") return
		const msUntilStale = updatedAt + TRANSLATION_STALE_AFTER_MS - Date.now()
		const timer = setTimeout(
			() => setNow(Date.now()),
			Math.max(msUntilStale, 0) + 500,
		)
		return () => clearTimeout(timer)
	}, [isRunning, updatedAt])

	return isRunning
}

export interface TranslationTarget extends TranslationProgress {
	code: string
	name: string
	hasExistingContent: boolean
}

export interface TranslationTargetsSummary {
	isRunning: boolean
	failedLanguageCodes: string[]
	// Failed languages plus any never translated, so a failure never hides a new language.
	retryLanguageCodes: string[]
	// Names of the languages a fresh run would overwrite.
	overwrittenLanguageNames: string[]
	// When the next running target goes stale, so the caller can re-render then.
	nextStaleAt: number | null
}

export function summarizeTranslationTargets(
	targets: readonly TranslationTarget[],
	now: number = Date.now(),
): TranslationTargetsSummary {
	const running = targets.filter((target) => isTranslationRunning(target, now))
	const staleTimes = running
		.map((target) => target.updatedAt)
		.filter((updatedAt): updatedAt is number => typeof updatedAt === "number")
		.map((updatedAt) => updatedAt + TRANSLATION_STALE_AFTER_MS)
	const isFailed = (target: TranslationTarget) =>
		target.translationStatus === "FAILED" || isTranslationStalled(target, now)
	const isUntranslated = (target: TranslationTarget) =>
		!target.translationStatus && !target.hasExistingContent
	return {
		isRunning: running.length > 0,
		failedLanguageCodes: targets.filter(isFailed).map((target) => target.code),
		retryLanguageCodes: targets
			.filter((target) => isFailed(target) || isUntranslated(target))
			.map((target) => target.code),
		overwrittenLanguageNames: targets
			.filter((target) => target.hasExistingContent)
			.map((target) => target.name),
		nextStaleAt: staleTimes.length > 0 ? Math.min(...staleTimes) : null,
	}
}

export function useTranslationTargetsSummary(
	targets: readonly TranslationTarget[],
): TranslationTargetsSummary {
	const [now, setNow] = useState(() => Date.now())
	const summary = summarizeTranslationTargets(targets, now)
	const { nextStaleAt } = summary

	useEffect(() => {
		if (nextStaleAt === null) return
		const timer = setTimeout(
			() => setNow(Date.now()),
			Math.max(nextStaleAt - Date.now(), 0) + 500,
		)
		return () => clearTimeout(timer)
	}, [nextStaleAt])

	return summary
}
